import { useCallback, useMemo } from 'react';
import {
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { MapPin, Store } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { deriveEmployerStatus } from '@duke-hq/shared-constants/employer';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { testIds } from '../testIds';
import { EmployerStatusSummary } from '../components/employerStatusDisplay';
import { EmployerDetailsTab } from './EmployerDetailsTab';
import { EmployersNotesTab } from './EmployersNotesTab';
import { EmployersPlacementsTab } from './EmployersPlacementsTab';
import { searchSuffix } from '../searchSuffix';
import { employerDetailPlaceholder } from './placeholder';
import type { EmployerListRow } from './types';

export function EmployerDetailPage({
  employerId,
  row,
}: {
  employerId: string;
  row?: EmployerListRow;
}): React.ReactElement {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    data: employer,
    isLoading,
    isPlaceholderData,
  } = trpcAdmin.workPlacements.employers.getEmployerDetail.useQuery(
    { employerId },
    { placeholderData: row != null ? employerDetailPlaceholder(row) : undefined },
  );

  const tabs = useMemo(
    () =>
      [
        { value: 'details', label: t`Employer Details`, isDisabled: false },
        { value: 'placements', label: t`Placements`, isDisabled: false },
        { value: 'notes', label: t`Notes`, isDisabled: false },
      ] as const,
    [],
  );

  const selectedTab = useMemo(() => {
    const index = tabs.findIndex((entry) => entry.value === tab && !entry.isDisabled);
    return index !== -1 ? index : 0;
  }, [tab, tabs]);

  const suffix = searchSuffix(searchParams);
  const handleTabsChange = useCallback(
    (index: number) => {
      const next = tabs[index];
      if (next != null) {
        navigate(`/placements/employers/${employerId}/${next.value}${suffix}`);
      }
    },
    [employerId, navigate, suffix, tabs],
  );

  if (employer == null) {
    return (
      <Center h="100%">
        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          <Text color="fg.muted">
            <Trans>Employer not found</Trans>
          </Text>
        )}
      </Center>
    );
  }

  const status = deriveEmployerStatus(employer);
  const location = [employer.addressLocality, employer.addressState].filter(Boolean).join(', ');

  return (
    <Flex direction="column" w="100%" h="100%" data-testid={testIds.employerDrawer}>
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        w="full"
        p={{ base: 2, md: 4 }}
      >
        <Stack gap={2}>
          <Text fontWeight="semibold" fontSize="2xl">
            {employer.name}
          </Text>
          <HStack gap={4} fontSize="sm" color="fg.muted" flexWrap="wrap">
            {employer.businessType != null ? (
              <HStack gap={1}>
                <Icon as={Store} boxSize={iconSize.sm} />
                <Text>{employer.businessType}</Text>
              </HStack>
            ) : null}
            {location !== '' ? (
              <HStack gap={1}>
                <Icon as={MapPin} boxSize={iconSize.sm} />
                <Text>{location}</Text>
              </HStack>
            ) : null}
          </HStack>
          <EmployerStatusSummary
            status={status}
            agreementExpiresAt={employer.agreementExpiresAt}
            insuranceExpiresAt={employer.insuranceExpiresAt}
          />
        </Stack>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={selectedTab}
          onChange={handleTabsChange}
        >
          <TabList bg="bg.subtle">
            {tabs.map((entry, index) => (
              <Tab
                key={entry.value}
                isDisabled={entry.isDisabled}
                bg={selectedTab === index ? 'bg.surface' : ''}
              >
                {entry.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels flex="1" overflow="auto">
            {tabs.map((entry) => (
              <TabPanel key={entry.value} p={0} h="100%">
                {entry.value === 'details' && !isPlaceholderData ? (
                  <EmployerDetailsTab employer={employer} />
                ) : null}
                {entry.value === 'details' && isPlaceholderData ? (
                  <Center p={8}>
                    <Spinner size="xl" />
                  </Center>
                ) : null}
                {entry.value === 'placements' ? (
                  <EmployersPlacementsTab employerId={employerId} />
                ) : null}
                {entry.value === 'notes' ? <EmployersNotesTab employerId={employerId} /> : null}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
